import React, { useContext, useEffect, useState } from 'react';
import { Card,  Col, Dropdown, DropdownButton, Form, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import DataTable from 'react-data-table-component';
import { BiPlus, BiTrash, BiXCircle } from "react-icons/bi";
import { SearchIcon } from '@primer/octicons-react';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import { useTranslation } from 'react-i18next';
import { UserContext } from "../UserContext";
import { translateAPIError } from '../utils/apiUtils';
import { hasAnyPerm } from '../utils/uiUtils';
import classes from './AdminPages.module.css';


const TemplateListView = (props) => {

  const { t }    = useTranslation(["common"]);
  const { user } = useContext(UserContext);

  const [searchTerms,  setSearchTerms]  = useState();

  const [objList,      setObjList]      = useState([]);
  const [,             setLoading]      = useState(false);
  const [totalRows,    setTotalRows]    = useState();
  const [perPage,      setPerPage]      = useState(25);
  const [currPage,     setCurrPage]     = useState(1);
  const [sortCol,      setSortCol]      = useState(null);
  const [sortDir,      setSortDir]      = useState(null);

  const [errMsg,       setErrMsg]       = useState('');
  const [notFoundMsg,  setNotFoundMsg]  = useState('');

  const [tableColumns, setTableColumns] = useState();

  const [searchField, setSearchField]   = useState(null);

  // ----- Data Table stuff -----

  const customTableStyles = {
    rows: {
      style: {
        minHeight: '25px', // override the row height
        fontSize: '11px',
      },
    },
    headCells: {
      style: {
        paddingLeft: '8px', // override the cell padding for head cells
        paddingRight: '8px',
      },
    },
    cells: {
      style: {
        paddingLeft: '8px', // override the cell padding for data cells
        paddingRight: '8px',
      },
    },
  };

  const handlePageChange = newPage => {
    setCurrPage(newPage);
    props.onSetPageState({searchField: searchField,
                          searchTerms: searchTerms,
                          sortCol:     sortCol,
                          sortDir:     sortDir,
                          perPage:     perPage,
                          currPage:    newPage});

    fetchObjs(searchField, searchTerms, sortCol, sortDir, perPage, newPage);
  };


  const handleRowsPerPageChange = async (newPerPage) => {

    setPerPage(newPerPage);
    props.onSetPageState({searchField: searchField,
                          searchTerms: searchTerms,
                          sortCol:     sortCol,
                          sortDir:     sortDir,
                          perPage:     newPerPage,
                          currPage:    1});

    fetchObjs(searchField, searchTerms, sortCol, sortDir, newPerPage, 1);
  };


  const handleSort = (column, sortDir) => {

    // instead of setTimeout this is where you would handle your API call.
    setSortCol (column.sortField);
    setSortDir (sortDir);

    props.onSetPageState({searchField: searchField,
                          searchTerms: searchTerms,
                          sortCol:     column.sortField,
                          sortDir:     sortDir,
                          perPage:     perPage,
                          currPage:    1});

    fetchObjs(searchField, searchTerms, column.sortField, sortDir, perPage, 1);
  };

  const paginationComponentOptions={
          rowsPerPageText: t("I18N_Rows_per_page"),
          rangeSeparatorText: t("I18N_Pagination_page_of_total"),
          noRowsPerPage: false,
          selectAllRowsItem: false,
          selectAllRowsItemText: 'All'
        };

  // ----- END - Data Table stuff -----


  // ----- Delete Confirmation Dialog -----

  const [deleteTarget,            setDeleteTarget]            = useState();
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [errMsgDeleteConfirm,     setErrMsgDeleteConfirm]     = useState('');

  function handleObjDelete(obj) {
    setDeleteTarget(obj);
    setShowDeleteConfirmDialog(true);
  }

  function handleObjDeleteCancel() {
    setDeleteTarget(null);
    setErrMsgDeleteConfirm('');
    setShowDeleteConfirmDialog(false);
  }

  // ----- END - Delete Confirmation Dialog -----


  // ----- General Initialization -----

  useEffect(() => {

    setSearchField(props.pageState.searchField);
    setSearchTerms(props.pageState.searchTerms);
    setSortCol(props.pageState.sortCol);
    setSortDir(props.pageState.sortDir);
    setPerPage(props.pageState.perPage);
    setCurrPage(props.pageState.currPage);

    fetchObjs(props.pageState.searchField, props.pageState.searchTerms, props.pageState.sortCol, props.pageState.sortDir,
              props.pageState.perPage, props.pageState.currPage);

    // Suppplement props that requires local code
    //let tmpTableColumns = JSON.parse(JSON.stringify(props.tableColumns));
    let tmpTableColumns = props.tableColumns;
    let primaryIdColumn = tmpTableColumns.find( ({ igcColumnType }) => igcColumnType === 'PRIMARY_ID' );

    if (primaryIdColumn) {
      // Replacing the cell here to inject handler for object detail
      primaryIdColumn.cell =
        (row => (
          <span style={{cursor:'pointer', color:'SteelBlue'}} onClick={() => handleObjDetails(row)}>
            { row[primaryIdColumn.igcFieldName] }
          </span>
        ));
    }

    if (!props.hideDeleteIcon) {
      let deleteColumn    = tmpTableColumns.find( ({ igcColumnType }) => igcColumnType === 'DELETE' );
      deleteColumn.cell =
        (row => (
            (!props.deletePermission || (props.deletePermission && hasAnyPerm(user, [props.deletePermission], true))) ?
              <OverlayTrigger overlay={<Tooltip id="tooltip-delete-obj" style={{fontSize:'11px'}}>{props.deleteObjTooltip}</Tooltip>}>
                <span style={{cursor:'pointer', color:'SteelBlue'}}
                      onClick={() => handleObjDelete(row)}><BiTrash size={16} />
                </span>
              </OverlayTrigger>
            : null
        ));
    }

    setTableColumns(tmpTableColumns);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.tableColumns]);


  // ----- General UI -----

  const onSearchFieldSelect = (key, event) => {
    setSearchField(key);
  };

  // ----- Backend Data Processing -----

  const fetchObjs = async (searchField, searchTerms, sortCol, sortDir, perPage, currPage) => {

    try {
      setLoading(true);

      const res = await props.axiosAPI.get(props.apiObjName + "?searchTerms=" +
                                           ((searchTerms && (searchTerms !== null)) ? encodeURIComponent(searchTerms) : '') +
                                           ((searchField && (searchField !== null)) ? ("&searchField=" + searchField) : '') +
                                           ((sortCol     && (sortCol     !== null)) ? ("&sortColumn=" + sortCol + "&sortDirection=" + sortDir) : '')  +
                                           ((perPage     && (perPage     !== null)) ? ("&limit="      + perPage)                               : '')  +
                                           ((currPage    && (currPage    !== null)) ? ("&offset="     + (currPage - 1) * perPage)              : ''));

      let objList = res.data.data;

      if (res.data.data.length === 0) {
        setNotFoundMsg("I18N_No_search_result");
      } else {
        setNotFoundMsg('');

        if (props.objListPostProcessing) {
          objList = props.objListPostProcessing (objList);
        } else {
          objList = res.data.data;
        }
      }

      setObjList(objList);

      setTotalRows(res.data.total);
      setErrMsg('');

      setLoading(false);
    } catch (error) {
      if (!error.message || (error.message !== 'IGNORE')) {
        setErrMsg(t(translateAPIError(error)));
      }
    }
  };


  function handleObjDeleteSubmit() {

    return new Promise(async (resolve, reject) => {
      try {

        const res = await props.axiosAPI.delete(props.apiObjName + "/" + deleteTarget.id);

        resolve(res.data);

        if (res.status === 200) {
          setErrMsgDeleteConfirm('');
          setShowDeleteConfirmDialog(false);

          // This will also cause a reload of the list.  Change to page 1 because in case deleting
          // the last item on the last page, refreshing the old last page will cause error.
          handlePageChange(1);
        }

      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsgDeleteConfirm(t(translateAPIError(error)));
        }
      }

    });
  }


  // ----- General UI Callbacks -----

  const onSearchInputChange = (e) => {
    setSearchTerms(e.target.value);
  }

  const handleSearchClear = ()  => {
    setSearchTerms('');

    setCurrPage(1); // Reset to page 1 for a fresh search
    props.onSetPageState({searchField: searchField,
                          searchTerms: '',
                          sortCol:     sortCol,
                          sortDir:     sortDir,
                          perPage:     perPage,
                          currPage:    1});

    fetchObjs(searchField, '', sortCol, sortDir, perPage, 1);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();

    setCurrPage(1); // Reset to page 1 for a fresh search
    props.onSetPageState({searchField: searchField,
                          searchTerms: searchTerms,
                          sortCol:     sortCol,
                          sortDir:     sortDir,
                          perPage:     perPage,
                          currPage:    1});

    fetchObjs(searchField, searchTerms, sortCol, sortDir, perPage, 1);
  }

  function handleObjDetails(obj) {
    props.onUIMode('DETAILS', obj);
  }

  const handleNewObj = (e) => {
    e.preventDefault();
    props.onUIMode('NEW')
  }


  return (
    <>
      <Card className='aaa' style={{border:'0', boxShadow:'0 2px 2px 0px rgba(0,0,0,0.1)', padding:'20px 40px 20px 40px',
                                    maxWidth:'2500px', height:'850px', width:'100%'}}>
        <div style={{ display: 'flex' }}>
          <span className={classes.admin_pages__page_title}>
            {props.listPageTitle}
          </span>
          <span style={{marginLeft:'auto', marginTop:'7px', color:'rgba(200,0,0)', fontSize:'12px'}}>
            {errMsg}
          </span>

          { !props.hideNewIcon ?
            <OverlayTrigger overlay={<Tooltip id="tooltip-new_obj" style={{fontSize:'11px'}}>{props.newObjTooltip}</Tooltip>}>
              <span onClick={handleNewObj} style={{ marginLeft: '0px', marginRight: '0px', marginTop: '3px', cursor: 'pointer',
                                                    color: 'rgb(100,100,100)' }}>
                <BiPlus size={18} />
              </span>
            </OverlayTrigger>
          : null }

          { props.searchFieldOptions ?
              <>
                <span style={{ marginLeft: '30px', marginTop: '8px', fontSize: '12px', minWidth: '30px' }}>
                  {t("I18N_Search_field")}:
                </span>
                <span style={{ marginLeft: '10px', marginRight: '0px', marginTop: '2px', minWidth: '20px' }}>
                  <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                    title={ !searchField ? t("common:I18N_DROPDOWN_PROMPT") : t("I18N_" + searchField) }
                    style={{ width: '130px' }}
                    onSelect={onSearchFieldSelect}>
                    {props.searchFieldOptions.map((item, index) => {
                      return (
                        <Dropdown.Item key={index} eventKey={item.key} className={classes.dropdown_item_wrap} style={{ width: '100%' }}>
                          {t("I18N_" + item.key)}
                        </Dropdown.Item>
                      );
                    })}
                  </DropdownButton>
                </span>
              </>
            : null
          }

          <span style={{ marginLeft: '20px', marginRight: '0px', width: '220px' }}>
            <Form onSubmit={handleSearchSubmit}>
              <Form.Group as={Row} >
                <Col sm="9" style={{ marginRight: '5px' }}>
                  <div style={{ position: 'relative' }}>
                    <Form.Control style={{ fontSize: '12px', paddingRight: '30px' }} type="text" name="searchTerms" value={searchTerms}
                                  onChange={(e) => onSearchInputChange(e)} placeholder={t("I18N_Search_terms")} />
                    {searchTerms && <BiXCircle size={15}
                                       style={{position:'absolute', right:'10px', top: '50%', transform:'translateY(-50%)',
                                               cursor:'pointer', color:'#aaa'}}
                                       onClick={handleSearchClear} />}
                  </div>
                </Col>
                <Col sm="2" style={{ padding: '5px' }}>
                  <OverlayTrigger placement="left" overlay={<Tooltip id="tooltip-search" style={{fontSize:'11px'}}>{props.searchTooltip}</Tooltip>}>
                    <span onClick={handleSearchSubmit} style={{ cursor: 'pointer', color: 'rgb(100,100,100)' }}>
                      <SearchIcon size={18} />
                    </span>
                  </OverlayTrigger>
                </Col>
              </Form.Group>
            </Form>
          </span>
        </div>

        { (notFoundMsg && notFoundMsg !== '') ?
          <span className={classes.admin_pages__field_value} style={{marginTop:'20px'}}>
            {t(notFoundMsg)}
          </span>
        :
          <div style={{ overflowY:'auto', fontSize:'11px', marginTop:'10px' }}>

            { objList && objList.length > 0 ?
              <DataTable
                columns={tableColumns}
                data={objList}
                customStyles={customTableStyles}
                fixedHeader
                fixedHeaderScrollHeight={'700px'}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationDefaultPage={currPage}
                paginationPerPage={perPage}
                paginationRowsPerPageOptions={[25,50,100]}
                onChangeRowsPerPage={handleRowsPerPageChange}
                onChangePage={handlePageChange}
                onSort={handleSort}
                sortServer
                highlightOnHover
                paginationComponentOptions={paginationComponentOptions}
                noDataComponent={t("I18N_No_search_result")}
              />
            : null
            }

          </div>
        }

        <DeleteConfirmDialog
          title={props.deleteConfirmDialogTitle}
          show={showDeleteConfirmDialog}
          targetName={deleteTarget ? deleteTarget.name : ''}
          errMsgDeleteConfirm={errMsgDeleteConfirm}
          onCancel={handleObjDeleteCancel}
          onSubmit={handleObjDeleteSubmit}
        />

      </Card>
    </>
  );
}


export default TemplateListView;
